import { FC, useMemo } from 'react';
import { CheckboxTree, CheckboxTreeGroup } from 'components/checkbox-tree';
import type { CountyOption } from './types';

interface Props {
  counties: CountyOption[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  disabled?: boolean;
}

const counties_ = (n: number) => `${n} ${n === 1 ? 'county' : 'counties'}`;
const states_ = (n: number) => `${n} ${n === 1 ? 'state' : 'states'}`;

// Counties grouped under their state. The gate matches on county id, so the
// state layer is purely how a human finds one of 3,235 rows — and why the same
// county name appearing in 30 states is no longer ambiguous.
export const CountyTree: FC<Props> = ({
  counties,
  selectedIds,
  onChange,
  disabled = false,
}) => {
  const groups = useMemo<CheckboxTreeGroup[]>(() => {
    const byState = new Map<number, CheckboxTreeGroup>();

    counties.forEach((county) => {
      let group = byState.get(county.stateId);
      if (!group) {
        group = {
          id: county.stateId,
          label: county.stateName,
          code: county.stateAbbreviation,
          items: [],
        };
        byState.set(county.stateId, group);
      }
      group.items.push({ id: county.id, label: county.name });
    });

    return [...byState.values()]
      .map((group) => ({
        ...group,
        items: [...group.items].sort((a, b) => a.label.localeCompare(b.label)),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [counties]);

  return (
    <CheckboxTree
      groups={groups}
      selectedIds={selectedIds}
      onChange={(ids) => onChange(ids as number[])}
      searchLabel="Search states or counties"
      searchPlaceholder="Search states or counties…"
      emptyMessage="No state or county matches your search."
      disabled={disabled}
      renderSummary={(count, stateCount) => {
        if (count === 0) {
          return 'No counties selected — auto-match runs everywhere (100% rollout)';
        }
        return `${counties_(count)} selected across ${states_(stateCount)}`;
      }}
    />
  );
};
